import bilAPI from "./api";

// The authoring surfaces ask for every language column with `?translations=1`;
// everything else keeps getting the single active-language string. The live
// observation form fetches the same version-detail endpoint, and it is the
// largest payload the app serves — tripling its text for every teacher who
// opens a lesson, to feed a screen only school admins ever see, is not a
// trade worth making.
const AUTHORING = { translations: 1 };

export const getRubricTemplates = ({ params: { is_active } = {}, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/rubrics/templates/`, {
          params: { is_active, ...AUTHORING },
          signal,
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const getRubricTemplate = ({ uuid, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/rubrics/templates/${uuid}/`, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const getRubricVersions = ({
  params: { template_code, status, version_number } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/rubrics/template-versions/`, {
          params: { template_code, status, version_number },
          signal,
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// Defaults to the authoring payload because every caller until now was an
// authoring surface; conducting a booked visit loads the SAME endpoint to get
// the version that visit was filed against, and passes `false` so a lesson
// observer is not served the editor's per-language columns (measured on the
// live 4-standard rubric: 9,126 bytes off vs 20,157 on).
export const getRubricVersion = ({ uuid, translations = true, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/rubrics/template-versions/${uuid}/`, {
          params: translations ? AUTHORING : undefined,
          signal,
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// `organization` resolves the active rubric for another in-scope school
// (head-org staff viewing a child); `teacher` (an in-scope account uuid) the
// one that teacher is scored on, as their focus areas are — organization wins
// when both are sent. Neither → the caller's own school.
export const getActiveRubric = ({
  params: { template_code, organization, teacher } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/rubrics/template-versions/active/`, {
          params: { template_code, organization, teacher },
          signal,
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const getAnswerOptionSets = ({ signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/rubrics/answer-option-sets/`, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// --- Template authoring (school managers, `rubrics.templates.manage`) ------

export const createTemplate = ({ body: { name, description, source_version } = {}, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .post(
          `/rubrics/templates/`,
          { name, description, source_version },
          { params: AUTHORING, signal },
        )
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const updateTemplate = ({ uuid, body = {}, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .patch(`/rubrics/templates/${uuid}/`, body, {
          params: AUTHORING,
          signal,
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// Opens a draft based on the given version: own template -> existing or new
// vN+1 draft; global template -> school-owned fork. Returns the draft tree.
// Deletes a template and every version under it, together with the schools'
// activations and the material rules built on its questions. Refused (400) only
// when something has been observed against it; `can_delete` on the list row
// says so up front, and `deletion-impact` spells out what else would go.
export const deleteTemplate = ({ uuid, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .delete(`/rubrics/templates/${uuid}/`, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// Dry run for the delete confirmation: versions, the schools whose activation
// would be cleared, how many material rules go with it, and whether deleting a
// global would leave no fallback rubric at all.
export const getTemplateDeletionImpact = ({ uuid, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/rubrics/templates/${uuid}/deletion-impact/`, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const startDraft = ({ uuid, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .post(`/rubrics/template-versions/${uuid}/draft/`, {}, {
          params: AUTHORING,
          signal,
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const updateDraftContent = ({ uuid, body: { standards } = {}, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .put(`/rubrics/template-versions/${uuid}/content/`, { standards }, {
          params: AUTHORING,
          signal,
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// Text-only fix on a PUBLISHED version — no new version, no row added or
// removed. `corrections` is a flat list of
// {row, uuid, field, expected: {lang: text}, value: {lang: text}}; `expected`
// is what the author was looking at, and a mismatch comes back as 409.
export const correctWording = ({ uuid, body: { corrections } = {}, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .patch(`/rubrics/template-versions/${uuid}/wording/`, { corrections }, {
          params: AUTHORING,
          signal,
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// Re-scope standards in place — any version short of retired, used or not, so
// moving a standard to other subjects never costs a new version. Body:
// {standards: [{uuid, subject_scope, subjects: [uuid]}]}; standards left out
// are untouched. Resolves the full version tree.
export const setSubjectScope = ({ uuid, body: { standards } = {}, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .patch(`/rubrics/template-versions/${uuid}/subject-scope/`, { standards }, {
          params: AUTHORING,
          signal,
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// Dry run for the publish dialog: which schools a `carry_activations` publish
// would move onto this version, how many inherit those rows, how many stay put.
export const getActivationCarry = ({ uuid, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/rubrics/template-versions/${uuid}/activation-carry/`, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const publishVersion = ({ uuid, carryActivations = false, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .post(
          `/rubrics/template-versions/${uuid}/publish/`,
          { carry_activations: carryActivations },
          { signal },
        )
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// Deletes ONE version (a draft, or a published one nothing was observed
// against) with its standards and questions; the template goes too when this
// was its only version. Refused with `version: "observations"` otherwise.
export const deleteVersion = ({ uuid, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .delete(`/rubrics/template-versions/${uuid}/`, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// --- School activation (singleton resource) ---------------------------------

export const getActivation = ({ signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/rubrics/activation/`, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const setActivation = ({ body: { template_version } = {}, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .put(`/rubrics/activation/`, { template_version }, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const resetActivation = ({ signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .delete(`/rubrics/activation/`, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};
